import React, { useState, useEffect } from 'react';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronUp, ChevronDown, TrendingUp, Users, PlayCircle, Mic2, BarChart3, Clock, Share2, Award, Heart, MessageCircle, ArrowUp } from 'lucide-react';
import StatCard from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  LineChart,
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell,
  ComposedChart,
  PieChart,
  Pie
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getAnalyticsStats,
  getWeeklyBopsData,
  getMonthlyGrowthData,
  getUserRetentionData,
  getWeeklyActiveUsersData,
  getAcquisitionSourceData,
  getEngagementData,
  getEngagementByTimeData,
  getEngagementRateData,
  getContentTypeData,
  getContentGrowthData,
  getTopPerformingPosts,
  getPopularToolsData
} from '@/lib/supabase';

const collaborationTrendsData = [
  { month: 'Jan', collaborations: 12 },
  { month: 'Feb', collaborations: 15 },
  { month: 'Mar', collaborations: 18 },
  { month: 'Apr', collaborations: 22 },
  { month: 'May', collaborations: 28 },
  { month: 'Jun', collaborations: 32 },
];

const userGrowthData = [
  { month: 'Jan', users: 1500 },
  { month: 'Feb', users: 2300 },
  { month: 'Mar', users: 3100 },
  { month: 'Apr', users: 4200 },
  { month: 'May', users: 5400 },
  { month: 'Jun', users: 7200 },
];

// Add these new data for growth analytics
const monthlyGrowthData = [
  { month: 'Jan', newUsers: 240, totalUsers: 1500, percentageGrowth: 19 },
  { month: 'Feb', newUsers: 350, totalUsers: 1850, percentageGrowth: 23 },
  { month: 'Mar', newUsers: 480, totalUsers: 2330, percentageGrowth: 26 },
  { month: 'Apr', newUsers: 620, totalUsers: 2950, percentageGrowth: 27 },
  { month: 'May', newUsers: 750, totalUsers: 3700, percentageGrowth: 25 },
  { month: 'Jun', newUsers: 890, totalUsers: 4590, percentageGrowth: 24 },
];

const userRetentionData = [
  { month: 'Jan', retention: 78 },
  { month: 'Feb', retention: 82 },
  { month: 'Mar', retention: 85 },
  { month: 'Apr', retention: 81 },
  { month: 'May', retention: 87 },
  { month: 'Jun', retention: 89 },
];

const acquisitionSourceData = [
  { source: 'Organic Search', users: 2450, percentage: 35 },
  { source: 'Social Media', users: 1890, percentage: 27 },
  { source: 'Direct Traffic', users: 1260, percentage: 18 },
  { source: 'Referrals', users: 840, percentage: 12 },
  { source: 'Email', users: 560, percentage: 8 },
];

const weeklyActiveUsersData = [
  { week: 'W1', users: 980 },
  { week: 'W2', users: 1120 },
  { week: 'W3', users: 1340 },
  { week: 'W4', users: 1450 },
  { week: 'W5', users: 1580 },
  { week: 'W6', users: 1720 },
  { week: 'W7', users: 1860 },
  { week: 'W8', users: 2010 },
];

// Define interface for analytics stats
interface AnalyticsStats {
  totalMembers: number;
  totalPosts: number;
  totalCollaborations: number;
  newMembersLastMonth: number;
  totalLikes: number;
  totalUpvotes: number;
  totalComments: number;
  topPerformingPost: {
    id: string;
    contentPreview: string;
    totalInteractions: number;
    likes: number;
    upvotes: number;
    comments: number;
  } | null;
  mostAwardedContent: {
    id: string;
    contentPreview: string;
    upvotes: number;
  } | null;
  averageEngagementRate: number | null;
  averageSessionDuration: number | null;
  mostSharedContent: any | null; // Define type more specifically if available
}

// Keep these reference data structures for fallback
const popularGenresData = [
  { genre: 'Hip Hop', posts: 312, engagement: 86 },
  { genre: 'Electronic', posts: 245, engagement: 72 },
  { genre: 'Pop', posts: 217, engagement: 78 },
  { genre: 'R&B', posts: 176, engagement: 84 },
  { genre: 'Indie', posts: 142, engagement: 65 },
  { genre: 'Rock', posts: 138, engagement: 62 },
  { genre: 'Jazz', posts: 72, engagement: 58 },
];

const contentPerformanceData = [
  { category: 'High Engagement', count: 380, percentage: 34 },
  { category: 'Medium Engagement', count: 456, percentage: 40 },
  { category: 'Low Engagement', count: 294, percentage: 26 },
];

const Analytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [weeklyBopsData, setWeeklyBopsData] = useState<any[]>([]);
  const [isWeeklyBopsLoading, setIsWeeklyBopsLoading] = useState(true);
  
  // Add state for the new growth data
  const [monthlyGrowthData, setMonthlyGrowthData] = useState<any[]>([]);
  const [isMonthlyGrowthLoading, setIsMonthlyGrowthLoading] = useState(true);
  
  const [userRetentionData, setUserRetentionData] = useState<any[]>([]);
  const [isRetentionLoading, setIsRetentionLoading] = useState(true);
  
  const [weeklyActiveUsersData, setWeeklyActiveUsersData] = useState<any[]>([]);
  const [isWeeklyActiveUsersLoading, setIsWeeklyActiveUsersLoading] = useState(true);
  
  const [acquisitionSourceData, setAcquisitionSourceData] = useState<any[]>([]);
  const [isAcquisitionSourceLoading, setIsAcquisitionSourceLoading] = useState(true);
  
  // Add state for engagement data
  const [isEngagementLoading, setIsEngagementLoading] = useState(true);
  const [engagementData, setEngagementData] = useState<any[]>([]);
  const [engagementByTimeData, setEngagementByTimeData] = useState<any[]>([]);
  const [engagementRateData, setEngagementRateData] = useState<any[]>([]);

  // Add state for content data - initialize with empty arrays
  const [isContentDataLoading, setIsContentDataLoading] = useState(true);
  const [contentTypeData, setContentTypeData] = useState<any[]>([]);
  const [contentGrowthData, setContentGrowthData] = useState<any[]>([]);
  const [topPerformingPostsData, setTopPerformingPostsData] = useState<any[]>([]);
  const [popularToolsData, setPopularToolsData] = useState<any[]>([]);

  // Add state for active tab
  const [activeTab, setActiveTab] = useState('overview');
  const [initialized, setInitialized] = useState(false);

  // Fetch stats when the component mounts
  useEffect(() => {
    const fetchStats = async () => {
      if (initialized) return;
      
      setIsLoading(true);
      setError(null);
      try {
        console.log("Fetching analytics stats...");
        const fetchedStats = await getAnalyticsStats();
        console.log("Got analytics stats:", fetchedStats);
        setStats(fetchedStats);
        setInitialized(true);
      } catch (err) {
        console.error('Error fetching analytics stats:', err);
        setError('Failed to load analytics data. Please try again later.');
        setStats(null); // Reset stats on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [initialized]);

  // Only fetch additional data after the main stats are loaded
  useEffect(() => {
    if (!initialized) return;
    
    // Fetch weekly bops data
    const fetchWeeklyBopsData = async () => {
      setIsWeeklyBopsLoading(true);
      try {
        const data = await getWeeklyBopsData();
        setWeeklyBopsData(data);
      } catch (err) {
        console.error('Error fetching weekly bops data:', err);
        // Use empty data on error
        setWeeklyBopsData([]);
      } finally {
        setIsWeeklyBopsLoading(false);
      }
    };

    // Fetch monthly growth data
    const fetchMonthlyGrowthData = async () => {
      setIsMonthlyGrowthLoading(true);
      try {
        const data = await getMonthlyGrowthData();
        setMonthlyGrowthData(data);
      } catch (err) {
        console.error('Error fetching monthly growth data:', err);
        setMonthlyGrowthData(monthlyGrowthData); // Use existing sample data on error
      } finally {
        setIsMonthlyGrowthLoading(false);
      }
    };
    
    // Fetch user retention data
    const fetchRetentionData = async () => {
      setIsRetentionLoading(true);
      try {
        const data = await getUserRetentionData();
        setUserRetentionData(data);
      } catch (err) {
        console.error('Error fetching user retention data:', err);
        setUserRetentionData(userRetentionData); // Use existing sample data on error
      } finally {
        setIsRetentionLoading(false);
      }
    };
    
    // Fetch weekly active users data
    const fetchWeeklyActiveUsers = async () => {
      setIsWeeklyActiveUsersLoading(true);
      try {
        const data = await getWeeklyActiveUsersData();
        setWeeklyActiveUsersData(data);
      } catch (err) {
        console.error('Error fetching weekly active users data:', err);
        setWeeklyActiveUsersData(weeklyActiveUsersData); // Use existing sample data on error
      } finally {
        setIsWeeklyActiveUsersLoading(false);
      }
    };
    
    // Fetch acquisition source data
    const fetchAcquisitionSources = async () => {
      setIsAcquisitionSourceLoading(true);
      try {
        const data = await getAcquisitionSourceData();
        setAcquisitionSourceData(data);
      } catch (err) {
        console.error('Error fetching acquisition source data:', err);
        setAcquisitionSourceData(acquisitionSourceData); // Use existing sample data on error
      } finally {
        setIsAcquisitionSourceLoading(false);
      }
    };

    // Fetch engagement data
    const fetchEngagementData = async () => {
      setIsEngagementLoading(true);
      try {
        // Get real engagement data from API
        const data = await getEngagementData();
        setEngagementData(data);
        
        // Get engagement by time of day data
        const timeData = await getEngagementByTimeData();
        setEngagementByTimeData(timeData);
        
        // Get engagement rate data
        const rateData = await getEngagementRateData();
        setEngagementRateData(rateData);
      } catch (error) {
        console.error('Error fetching engagement data:', error);
        
        // Fallback to sample data if there's an error
        setEngagementData([
          { date: 'Jan', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
          { date: 'Feb', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
          { date: 'Mar', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
          { date: 'Apr', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
          { date: 'May', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
          { date: 'Jun', likes: 0, comments: 0, shares: 0, activeUsers: 0 },
        ]);
        
        // Time of day data
        setEngagementByTimeData(Array.from({ length: 24 }, (_, hour) => ({
          hour: `${hour}:00`,
          engagementCount: 0
        })));
        
        // Engagement rate data
        setEngagementRateData([
          { month: 'Jan', engagementRate: 0 },
          { month: 'Feb', engagementRate: 0 },
          { month: 'Mar', engagementRate: 0 },
          { month: 'Apr', engagementRate: 0 },
          { month: 'May', engagementRate: 0 },
          { month: 'Jun', engagementRate: 0 }
        ]);
      } finally {
        setIsEngagementLoading(false);
      }
    };

    // Fetch content data
    const fetchContentData = async () => {
      setIsContentDataLoading(true);
      try {
        // Get content type distribution data
        const typeData = await getContentTypeData();
        setContentTypeData(typeData);
        
        // Get content growth data
        const growthData = await getContentGrowthData();
        setContentGrowthData(growthData);
        
        // Get top performing posts
        const topPostsData = await getTopPerformingPosts();
        setTopPerformingPostsData(topPostsData);
        
        // Get popular tools data
        const toolsData = await getPopularToolsData();
        setPopularToolsData(toolsData);
      } catch (error) {
        console.error('Error fetching content data:', error);
        
        // Fallback to empty data if there's an error
        setContentTypeData([]);
        setContentGrowthData([]);
        setTopPerformingPostsData([]);
        setPopularToolsData([]);
      } finally {
        setIsContentDataLoading(false);
      }
    };

    fetchWeeklyBopsData();
    fetchMonthlyGrowthData();
    fetchRetentionData();
    fetchWeeklyActiveUsers();
    fetchAcquisitionSources();
    fetchEngagementData();
    fetchContentData(); // Add content data fetching
  }, [initialized]);

  const calculateGrowthPercentage = (data: any[]) => {
    if (!data || data.length < 2) return 0;
    
    // Get the last two months' data for comparison
    const currentMonth = data[data.length - 1];
    const previousMonth = data[data.length - 2];
    
    if (!currentMonth || !previousMonth) return 0;
    
    // Calculate percentage change
    if (previousMonth.totalUsers === 0) return 100; // If starting from zero, it's 100% growth
    
    return Math.round((currentMonth.totalUsers - previousMonth.totalUsers) / previousMonth.totalUsers * 100);
  };
  
  const getLatestRetentionRate = (data: any[]) => {
    if (!data || data.length === 0) return "N/A";
    // Get the most recent retention rate
    const latestData = data[data.length - 1];
    return latestData?.retention || "N/A";
  };
  
  const getLatestWeeklyActiveUsers = (data: any[]) => {
    if (!data || data.length === 0) return "0";
    // Get the most recent weekly active users count
    const latestData = data[data.length - 1];
    return latestData?.users || "0";
  };
  
  const getNewMembersChange = () => {
    if (!stats || !monthlyGrowthData || monthlyGrowthData.length < 2) return 0;
    
    // Get the last two months' data for comparison
    const currentMonth = monthlyGrowthData[monthlyGrowthData.length - 1];
    const previousMonth = monthlyGrowthData[monthlyGrowthData.length - 2];
    
    if (!currentMonth || !previousMonth) return 0;
    
    // Calculate percentage change in new users
    if (previousMonth.newUsers === 0) return 100; // If starting from zero, it's 100% growth
    
    return Math.round((currentMonth.newUsers - previousMonth.newUsers) / previousMonth.newUsers * 100);
  };
  
  const getWeeklyActiveUsersChange = (data: any[]) => {
    if (!data || data.length < 2) return 0;
    
    // Get the last two weeks' data for comparison
    const currentWeek = data[data.length - 1];
    const previousWeek = data[data.length - 2];
    
    if (!currentWeek || !previousWeek) return 0;
    
    // Calculate percentage change
    if (previousWeek.users === 0) return 100; // If starting from zero, it's 100% growth
    
    return Math.round((currentWeek.users - previousWeek.users) / previousWeek.users * 100);
  };

  const renderWeeklyBopsChart = () => {
    if (isWeeklyBopsLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!weeklyBopsData.length) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No bops activity data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Start creating bops to see your activity metrics</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer 
          config={{
            bops: { theme: { light: "#FF6384", dark: "#FF6384" } }
          }}
          className="w-full h-full"
        >
          <AreaChart
            data={weeklyBopsData}
            margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="day" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-medium">{label}</div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{payload[0].name}</span>
                        <span className="font-mono font-medium tabular-nums text-foreground">{payload[0].value}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="bops"
              name="bops"
              stroke="var(--color-bops)"
              fill="var(--color-bops)"
              fillOpacity={0.3}
            />
          </AreaChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderCollaborationTrendsChart = () => {
    if (isLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!stats || !collaborationTrendsData.length) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No collaboration data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Start collaborating with other users to see your metrics</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer 
          config={{
            collaborations: { theme: { light: "#4BC0C0", dark: "#4BC0C0" } }
          }}
          className="w-full h-full"
        >
          <BarChart
            data={collaborationTrendsData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="month" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-medium">{label}</div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{payload[0].name}</span>
                        <span className="font-mono font-medium tabular-nums text-foreground">{payload[0].value}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar 
              dataKey="collaborations" 
              name="collaborations" 
              fill="var(--color-collaborations)" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderUserGrowthChart = () => {
    if (isLoading || isMonthlyGrowthLoading) {
      return <Skeleton className="h-[400px] w-full" />;
    }

    if (!monthlyGrowthData || monthlyGrowthData.length === 0) {
      return (
        <div className="h-[400px] flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No user growth data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Check back later as users join the platform</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer 
          config={{
            totalUsers: { theme: { light: "#FF6384", dark: "#FF6384" } },
            newUsers: { theme: { light: "#36A2EB", dark: "#36A2EB" } }
          }}
          className="w-full h-full"
        >
          <LineChart
            data={monthlyGrowthData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="month" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Total Users: {payload[0]?.value}</div>
                      <div>New Users: {payload[1]?.value}</div>
                      <div>Growth: {payload.find(p => p.dataKey === 'percentageGrowth')?.value}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="totalUsers" 
              name="Total Users" 
              stroke="var(--color-totalUsers)" 
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="newUsers" 
              name="New Users" 
              stroke="var(--color-newUsers)" 
              strokeWidth={1.5}
            />
          </LineChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  // Add a new render function for the growth charts
  const renderMonthlyGrowthChart = () => {
    if (isLoading || isMonthlyGrowthLoading) {
      return <Skeleton className="h-[400px] w-full" />;
    }

    if (!monthlyGrowthData || monthlyGrowthData.length === 0) {
      return (
        <div className="h-[400px] flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No monthly growth data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Check back later as users join the platform</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer 
          config={{
            newUsers: { theme: { light: "#36A2EB", dark: "#36A2EB" } },
            percentageGrowth: { theme: { light: "#FF6384", dark: "#FF6384" } }
          }}
          className="w-full h-full"
        >
          <ComposedChart
            data={monthlyGrowthData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>New Users: {payload[0]?.value}</div>
                      <div>Growth Rate: {payload[1]?.value}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="newUsers"
              name="New Users"
              fill="var(--color-newUsers)"
              barSize={30}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="percentageGrowth"
              name="Growth Rate"
              stroke="var(--color-percentageGrowth)"
              strokeWidth={2}
            />
          </ComposedChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderRetentionChart = () => {
    if (isRetentionLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            retention: { theme: { light: "#8884d8", dark: "#8884d8" } }
          }}
        >
          <LineChart
            width={500}
            height={300}
            data={userRetentionData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 100]} />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Retention Rate: {payload[0].value}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="retention" 
              name="Retention %" 
              stroke="#8884d8" 
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          </LineChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderWeeklyActiveUsersChart = () => {
    if (isWeeklyActiveUsersLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            users: { theme: { light: "#8884d8", dark: "#8884d8" } }
          }}
        >
          <AreaChart
            width={500}
            height={300}
            data={weeklyActiveUsersData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Active Users: {payload[0].value}</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area type="monotone" dataKey="users" name="Active Users" stroke="#8884d8" fill="#8884d8" />
          </AreaChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderAcquisitionSourceChart = () => {
    if (isAcquisitionSourceLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            users: { theme: { light: "#0088FE", dark: "#0088FE" } },
            percentage: { theme: { light: "#00C49F", dark: "#00C49F" } }
          }}
        >
          <BarChart
            width={500}
            height={300}
            data={acquisitionSourceData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
            barSize={20}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="source" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Users: {payload[0].value}</div>
                      <div>Percentage: {payload[0].payload.percentage}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Bar dataKey="users" name="Users">
              {acquisitionSourceData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  // Add new render functions for content charts
  const renderContentTypeChart = () => {
    if (isContentDataLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!contentTypeData || contentTypeData.length === 0) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No content type data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Add posts with different content types to see analytics</p>
        </div>
      );
    }

    const COLORS = ['#FF6384', '#36A2EB', '#FFCD56', '#4BC0C0', '#9966FF'];

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            percentage: { theme: { light: "#FF6384", dark: "#FF6384" } }
          }}
          className="w-full h-full"
        >
          <BarChart
            data={contentTypeData}
            layout="vertical"
            margin={{ top: 20, right: 20, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={true} vertical={false} />
            <XAxis type="number" />
            <YAxis type="category" dataKey="type" />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-medium">{payload[0].name}</div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Percentage</span>
                        <span className="font-mono font-medium tabular-nums text-foreground">{payload[0].value}%</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar 
              dataKey="percentage" 
              name="percentage"
              radius={[0, 4, 4, 0]}
            >
              {contentTypeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderPopularToolsChart = () => {
    if (isContentDataLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!popularToolsData || popularToolsData.length === 0) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No tools data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Add posts with tools to see analytics</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            posts: { theme: { light: "#36A2EB", dark: "#36A2EB" } },
            engagement: { theme: { light: "#FF6384", dark: "#FF6384" } }
          }}
          className="w-full h-full"
        >
          <BarChart
            data={popularToolsData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="tool" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Posts: {payload[0].value}</div>
                      <div>Engagement: {payload[1].value}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar 
              dataKey="posts" 
              name="posts" 
              fill="var(--color-posts)" 
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="engagement" 
              name="engagement" 
              fill="var(--color-engagement)" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderContentGrowthChart = () => {
    if (isContentDataLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!contentGrowthData || contentGrowthData.length === 0) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No content growth data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Check back later to see content growth over time</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            posts: { theme: { light: "#FF6384", dark: "#FF6384" } },
            comments: { theme: { light: "#36A2EB", dark: "#36A2EB" } },
            shares: { theme: { light: "#FFCD56", dark: "#FFCD56" } }
          }}
          className="w-full h-full"
        >
          <LineChart
            data={contentGrowthData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="month" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Posts: {payload[0].value}</div>
                      <div>Comments: {payload[1].value}</div>
                      <div>Shares: {payload[2].value}</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Line 
              type="monotone" 
              dataKey="posts" 
              name="posts" 
              stroke="var(--color-posts)" 
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="comments" 
              name="comments" 
              stroke="var(--color-comments)" 
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="shares" 
              name="shares" 
              stroke="var(--color-shares)" 
              strokeWidth={2}
            />
          </LineChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderEngagementChart = () => {
    if (isEngagementLoading) {
      return <Skeleton className="h-80 w-full" />;
    }

    if (!engagementData || engagementData.length === 0) {
      return (
        <div className="h-80 flex flex-col items-center justify-center">
          <p className="text-vibebops-textMuted mb-4">No engagement data available yet</p>
          <p className="text-sm text-vibebops-textMuted mb-4">Check back later as users engage with the platform</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            likes: { theme: { light: "#8884d8", dark: "#8884d8" } },
            comments: { theme: { light: "#82ca9d", dark: "#82ca9d" } },
            shares: { theme: { light: "#ffc658", dark: "#ffc658" } },
            activeUsers: { theme: { light: "#ff8042", dark: "#ff8042" } }
          }}
          className="w-full h-full"
        >
          <LineChart
            data={engagementData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="date" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      {payload.map((entry, index) => (
                        <div key={`item-${index}`} className="flex justify-between">
                          <span className="text-muted-foreground">{entry.name}</span>
                          <span className="font-mono font-medium tabular-nums text-foreground">{entry.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="likes" 
              name="Likes" 
              stroke="var(--color-likes)" 
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="comments" 
              name="Comments" 
              stroke="var(--color-comments)" 
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="shares" 
              name="Shares" 
              stroke="var(--color-shares)" 
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="activeUsers" 
              name="Active Users" 
              stroke="var(--color-activeUsers)" 
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderEngagementByTimeChart = () => {
    if (isEngagementLoading) {
      return <Skeleton className="h-80 w-full" />;
    }
    
    if (!engagementByTimeData || engagementByTimeData.length === 0) {
      return (
        <div className="flex items-center justify-center h-80 w-full border rounded-md">
          <p className="text-zinc-500">No engagement by time data available</p>
        </div>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            engagementCount: { theme: { light: "#8884d8", dark: "#8884d8" } }
          }}
          className="w-full h-full"
        >
          <BarChart
            data={engagementByTimeData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="hour" />
            <YAxis />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Engagements: {payload[0].value}</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Bar 
              dataKey="engagementCount" 
              name="Engagements"
              fill="#8884d8" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  const renderEngagementRateChart = () => {
    if (isEngagementLoading) {
      return <Skeleton className="h-80 w-full" />;
    }
    
    if (!engagementRateData || engagementRateData.length === 0) {
      return (
        <div className="flex items-center justify-center h-80 w-full border rounded-md">
          <p className="text-zinc-500">No engagement rate data available</p>
        </div>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartContainer
          config={{
            engagementRate: { theme: { light: "#FF6384", dark: "#FF6384" } }
          }}
          className="w-full h-full"
        >
          <LineChart
            data={engagementRateData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
            <ChartTooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                      <div className="font-bold">{label}</div>
                      <div>Engagement Rate: {payload[0].value}%</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="engagementRate" 
              name="Engagement Rate" 
              stroke="var(--color-engagementRate)" 
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ChartContainer>
      </ResponsiveContainer>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-extrabold">Analytics Dashboard</h1>
            <p className="text-vibebops-textMuted mt-1">Track and analyze all VibeBops platform activity</p>
          </div>
        </div>

        {error && (
          <Card className="bg-red-100 border-red-500 text-red-700">
            <CardHeader>
              <CardTitle>Error Loading Data</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{error}</p>
            </CardContent>
          </Card>
        )}

        {isLoading && !initialized ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array(4).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : (
          // Only show tabs once data is loaded
          <Tabs defaultValue="overview" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="bg-vibebops-highlight border-4 border-black rounded-md mb-4 p-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="growth">Growth</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="Total Members" 
                  value={stats?.totalMembers.toString()} 
                  isLoading={isLoading}
                  icon={Users}
                />
                <StatCard 
                  title="Collaborations" 
                  value={stats?.totalCollaborations.toString()}
                  isLoading={isLoading}
                  icon={Users}
                />
                <StatCard 
                  title="New Members (30d)" 
                  value={stats?.newMembersLastMonth.toString()} 
                  isLoading={isLoading}
                  icon={Users}
                />
                <StatCard 
                  title="Total Bops" 
                  value={stats?.totalPosts.toString()} 
                  isLoading={isLoading}
                  icon={PlayCircle}
                />
              </div>

              <Card className="shadow-sm border-4 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <PlayCircle className="h-5 w-5 text-vibebops-accent" />
                    Weekly Bops Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {renderWeeklyBopsChart()}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-vibebops-accent" />
                      Platform Growth
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[400px]">
                      {renderUserGrowthChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="engagement" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatCard 
                  title="Total Likes" 
                  value={stats?.totalLikes.toString()} 
                  isLoading={isLoading}
                  icon={Heart}
                />
                <StatCard 
                  title="Total Upvotes" 
                  value={stats?.totalUpvotes.toString()} 
                  isLoading={isLoading}
                  icon={ArrowUp}
                />
                <StatCard 
                  title="Total Comments" 
                  value={stats?.totalComments.toString()} 
                  isLoading={isLoading}
                  icon={MessageCircle}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Users className="h-5 w-5 text-vibebops-accent" />
                      Collaboration Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderCollaborationTrendsChart()}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Heart className="h-5 w-5 text-vibebops-accent" />
                      Engagement Over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderEngagementChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-vibebops-accent" />
                      Engagement by Time of Day
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderEngagementByTimeChart()}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-vibebops-accent" />
                      Monthly Engagement Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderEngagementRateChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="growth" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="Total Members" 
                  value={stats?.totalMembers.toString()} 
                  change={calculateGrowthPercentage(monthlyGrowthData)}
                  period="last month"
                  isLoading={isLoading || isMonthlyGrowthLoading}
                  icon={Users}
                />
                <StatCard 
                  title="New Members (30d)" 
                  value={stats?.newMembersLastMonth.toString()} 
                  change={getNewMembersChange()}
                  period="previous 30d"
                  isLoading={isLoading || isMonthlyGrowthLoading}
                  icon={Users}
                />
                <StatCard 
                  title="User Retention" 
                  value={`${getLatestRetentionRate(userRetentionData)}%`}
                  change={userRetentionData && userRetentionData.length > 1 ? 
                    userRetentionData[userRetentionData.length - 1].retention - userRetentionData[userRetentionData.length - 2].retention : 0}
                  period="last month"
                  isLoading={isRetentionLoading}
                  icon={TrendingUp}
                />
                <StatCard 
                  title="Weekly Active Users" 
                  value={getLatestWeeklyActiveUsers(weeklyActiveUsersData)} 
                  change={getWeeklyActiveUsersChange(weeklyActiveUsersData)}
                  period="last week"
                  isLoading={isWeeklyActiveUsersLoading}
                  icon={Users}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-vibebops-accent" />
                      Monthly Growth
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderMonthlyGrowthChart()}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Users className="h-5 w-5 text-vibebops-accent" />
                      User Retention Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderRetentionChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Users className="h-5 w-5 text-vibebops-accent" />
                      Weekly Active Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderWeeklyActiveUsersChart()}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-vibebops-accent" />
                      User Acquisition Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderAcquisitionSourceChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="content" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="Total Content" 
                  value={stats?.totalPosts.toString()} 
                  change={16}
                  period="last month"
                  isLoading={isLoading}
                  icon={PlayCircle}
                />
                <StatCard 
                  title="Avg. Engagement Rate" 
                  value="74%" 
                  change={8}
                  period="last month"
                  isLoading={isLoading}
                  icon={Heart}
                />
                <StatCard 
                  title="Comments per Post" 
                  value="2.3" 
                  change={12}
                  period="last month"
                  isLoading={isLoading}
                  icon={MessageCircle}
                />
                <StatCard 
                  title="Shares per Post" 
                  value="0.9" 
                  change={23}
                  period="last month"
                  isLoading={isLoading}
                  icon={Share2}
                />
              </div>

              <div className="grid grid-cols-1 gap-6">
                <Card className="shadow-sm border-4 border-black">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Mic2 className="h-5 w-5 text-vibebops-accent" />
                      Popular Tools
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      {renderPopularToolsChart()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="shadow-sm border-4 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-vibebops-accent" />
                    Content Growth
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {renderContentGrowthChart()}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-4 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <Award className="h-5 w-5 text-vibebops-accent" />
                    Top Performing Content
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isContentDataLoading ? (
                    <Skeleton className="h-60 w-full" />
                  ) : !topPerformingPostsData || topPerformingPostsData.length === 0 ? (
                    <div className="h-60 flex flex-col items-center justify-center">
                      <p className="text-vibebops-textMuted mb-4">No top performing posts available yet</p>
                      <p className="text-sm text-vibebops-textMuted mb-4">Create more content to see performance metrics</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs uppercase bg-vibebops-highlight">
                          <tr>
                            <th scope="col" className="px-6 py-3 rounded-l-lg">Title</th>
                            <th scope="col" className="px-6 py-3">Creator</th>
                            <th scope="col" className="px-6 py-3">Likes</th>
                            <th scope="col" className="px-6 py-3">Comments</th>
                            <th scope="col" className="px-6 py-3 rounded-r-lg">Shares</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topPerformingPostsData.map((post) => (
                            <tr key={post.id} className="bg-vibebops-card border-b">
                              <td className="px-6 py-4 font-medium">{post.title}</td>
                              <td className="px-6 py-4">{post.author}</td>
                              <td className="px-6 py-4">{post.likes}</td>
                              <td className="px-6 py-4">{post.comments}</td>
                              <td className="px-6 py-4">{post.shares}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </MainLayout>
  );
};

export default Analytics;
